import { CurrencyPipe, PercentPipe } from '@angular/common';
import { Injectable, isDevMode } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { AccountInfoActions } from '../../account-info-header/actions';
import { selectcancelledTransactionId } from '../../account-info-header/reducers';
import { MappedStatusViewResponse, StatusViewResponse } from '../../shared/models/StatusViewResponse';
import { StatusViewService } from '../../shared/services/status-view.service';
import { StandingInstruction } from '../../shared/types/StandingInstruction';
import { MediaTypeCd, TransactionDirection } from '../../shared/types/Transaction';
import { Constants, MediaTypeCdDict, achRecTypeCodes, transactionDirection } from '../../shared/utilities/Constants';
import { StandingAuthConfirmationPageActions } from '../../standing-authorizations/actions';
import { AgreementActions, ConfirmationPageActions, StandingAuthorizationsTableActions, TransactionsTableActions } from '../actions';
import { Sloa } from '../models';
import { ServiceError } from '../models/shared/ServiceError';
import { SharedActions } from '../../shared/actions';
import { InitReleaseActions } from '../../initiate-and-release/actions';
import { concatLatestFrom } from "@ngrx/operators";
import { CoreService } from '@charlesschwab/sac-ui-core';
import { ContextType } from '@charlesschwab/as-ui-core';
import { ValidateWireFlagsService } from '../../shared/services/validate-WireFlags.service';

@Injectable()
export class CreateRequestEffects {

  loadTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccountInfoActions.loadTransactions,
        TransactionsTableActions.refresh,
        StandingAuthorizationsTableActions.refresh,
        ConfirmationPageActions.viewTransactionStatusClicked,
        ConfirmationPageActions.createAnotherRequestClicked,
        StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
        InitReleaseActions.refresh,
        AccountInfoActions.refresh
      ),
      concatLatestFrom(() => [
        this.store.select(selectcancelledTransactionId)
      ]),
      exhaustMap(([action, id]) => {
        let isPalAccount = this.coreService.currentContext.contextType == ContextType.PALAccount && this.validateWireFlagsService.enablePalWireCreateTransaction();
        if (
          action.accountId.trim().length === Constants.minAccountNumberLength
          || action.accountId.trim().length === Constants.maxAccountNumberLength
          || isPalAccount
        ) {
          return this.statusViewService
            .getAccountOverview(action.accountId, isPalAccount)
            .pipe(
              map((statusViewResponse: StatusViewResponse) => {
                let standingInstructions = statusViewResponse.cashieringSloas.filter(elem => elem.mediaTypeCd !== '').map((sloa) => {
                  return createInstructionFromOverviewSloa(sloa, statusViewResponse.isIip)
                });
                return AccountInfoActions.loadTransactionsSuccess(mapApiResponseToOverview(statusViewResponse, standingInstructions, id))
              }
              ),
              catchError(async (err: ServiceError) => {
                if (isDevMode()) {
                  return SharedActions.displayFilePickerModal({content: 'Overview'});
                }
                return AccountInfoActions.loadTransactionsFailure(err);
              })
            );
        } else {
          let err: ServiceError = <ServiceError>{ message: 'Invalid Account number entered' }
          return of(
            AccountInfoActions.loadTransactionsFailure(err)
          );
        }
      })
    )
  );

  captureFirstTimeUserAgreement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgreementActions.submitFirstTimeUserAgreement),
      switchMap(() => {
        return this.statusViewService.captureAdvisorAgreement(true).pipe( //advisor can only accept agreement
          switchMap(() => {
            return of(AgreementActions.submitFirstTimeUserAgreementSuccess());
          }),
          catchError(() => of(AgreementActions.submitFirstTimeUserAgreementFailure({
            errorMsg: 'Failed to capture advisor agreement'
          })))
        )
      })
    )
  );

  constructor(
    private actions$: Actions,
    private statusViewService: StatusViewService,
    private store: Store,
    private coreService: CoreService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { }
}

export function mapApiResponseToOverview(statusViewResponse: StatusViewResponse, standingInstructions: StandingInstruction[], id: string): MappedStatusViewResponse {
  const modifiedResponse = {
    ...statusViewResponse,
    mappedInstructions: standingInstructions,
    isTrust: false,
    trustIncomeBalance: '',
    trustPrincipalBalance: ''
  };
  let currencyPipe = new CurrencyPipe("en-US");
  let percentPipe = new PercentPipe("en-US");

  const cashieringTransactionsMap = statusViewResponse.cashieringTransactions.map(element => {
    const localEle = { ...element };
    if (typeof element.incomingAmount === 'number') {
      localEle.fundsDirection = transactionDirection.in
      localEle.incomingAmount = currencyPipe.transform(element.incomingAmount)
    }
    if (typeof element.outgoingAmount === 'number') {
      localEle.fundsDirection = transactionDirection.out
      localEle.outgoingAmount = currencyPipe.transform(element.outgoingAmount)
    }
    if (element.cashieringRqstId === id){
      localEle.isCancelable = false;
      localEle.isEditable = false;
    }
    return localEle;
  });

  const cashieringRecurringTransactionsMap = statusViewResponse.cashieringRecurringTransactions.map(element => {
    const localEle = { ...element };
    if (typeof element.incomingAmount === 'number' && element.incomingAmount >= 0) {
      localEle.incomingAmount = localEle.isPercent ? percentPipe.transform(element.incomingAmount/100) : currencyPipe.transform(element.incomingAmount)
    }
    if (typeof element.outgoingAmount === 'number' && element.outgoingAmount >= 0) {
      localEle.outgoingAmount = localEle.isPercent ? percentPipe.transform(element.outgoingAmount/100) : currencyPipe.transform(element.outgoingAmount)
    }
    if (element.mediaTypeCd === 'A') {
      localEle.txnCode = achRecTypeCodes.recurring
    }
    if(element.cashieringRqstId === id){
      localEle.isCancelable = false;
      localEle.isEditable = false;
    }
    return localEle;
  });

  modifiedResponse.agreement = statusViewResponse.agreement;
  modifiedResponse.cashieringRecurringTransactions = cashieringRecurringTransactionsMap;
  modifiedResponse.cashieringTransactions = cashieringTransactionsMap;
  modifiedResponse.hasMoveMoneyDataAccess = statusViewResponse.hasMoveMoneyDataAccess;
  modifiedResponse.accountDisplayDetails = statusViewResponse.accountDisplayDetails;
  modifiedResponse.transactionAuthorizationDetails = statusViewResponse.transactionAuthorizationDetails;
  modifiedResponse.iraDetails = statusViewResponse.iraDetails;
  modifiedResponse.isIra = statusViewResponse.isIra;
  modifiedResponse.isTrust = statusViewResponse.isTrust;
  modifiedResponse.trustIncomeBalance = statusViewResponse.ptrsDetails?.trustIncomeBalance ?? '0.00';
  modifiedResponse.trustPrincipalBalance = statusViewResponse.ptrsDetails?.trustPrincipalBalance ?? '0.00';
  modifiedResponse.isIip = statusViewResponse.isIip;
  modifiedResponse.isValidRegType = statusViewResponse.isValidRegType;
  return modifiedResponse;
}

export function createInstructionFromOverviewSloa(sloa: Sloa,  isIip: boolean): StandingInstruction {
  let instruction: StandingInstruction = {
    nickname: sloa.nickname,
    cashieringRqstId: sloa.cashieringRqstId,
    cashieringRqstSecondId: sloa.cashieringRqstSecondId,
    fundsDirection: sloa.fundsDirection ? sloa.fundsDirection as TransactionDirection : (sloa.incomingAmount != null) ? 'Incoming' : 'Outgoing',
    transactionType: MediaTypeCdDict[sloa.mediaTypeCd as MediaTypeCd],
    to1: sloa.to1,
    to2: sloa.to2,
    to3: sloa.to3,
    address: sloa.address,
    statusCd: sloa.statusCd,
    statusCodeDescription: sloa.statusCodeDescription,
    masterAccountId: sloa.masterAccountId,
    isViewable: sloa.isViewable,
    isCancelable: sloa.isCancelable,
    isEditable: sloa.isEditable,
    isReactivatable: sloa.isReactivatable,
    isThirdParty: sloa.thirdPartyInd,
    isRequestTransaction: sloa.isRequestTrans,
    isBundled: sloa.bundledSloaIn,
    descriptionSuffix: formatDescriptionSuffix(sloa.typeDescription, sloa.thirdPartyInd),
    processDate: sloa.processDate,
    incomingAmount: sloa.incomingAmount,
    outgoingAmount: sloa.outgoingAmount,
    isPercent: sloa.isPercent,
    isRecurring: sloa.isRecurring,
    txnCode: sloa.txnCode,
    frequencyCode: sloa.frequencyCode,
    frequencyDescription: sloa.frequencyDescription,
    isStandingInstruction: false,
    isSloaSetup: sloa.isSloaSetup,
    faMasterAcct: sloa.faMasterAcct,
    mediaTypeCd: sloa.mediaTypeCd
  }

  if (sloa.mediaTypeCd === 'A') {
    instruction.txnCode = achRecTypeCodes.onRequest
    if((sloa.frequencyDescription || sloa.typeDescription.includes("Recurring")) || (!sloa.isRequestTrans && !sloa.typeDescription.includes("On-Request")))
    {
      instruction.txnCode = achRecTypeCodes.recurring
    }
  }
  if (isIip && sloa.fundsDirection === "Outgoing") {
    instruction.isEditable = false;
    instruction.isCancelable = false;
  }

  return instruction;
}

export function formatDescriptionSuffix(typeDescription: string, isThirdParty: boolean) {
  var descriptionSuffix = typeDescription;
  if (typeDescription.includes("Set-Up")) {
    if (typeDescription.split('-')[3] == undefined) {
      descriptionSuffix = "Set-Up - " + typeDescription.split('-')[2];
    }
    else {
      descriptionSuffix = "Set-Up - " + typeDescription.split('-')[3];
    }
  }
  else if (typeDescription.split('-')[1] === ' Bundled') {
    descriptionSuffix = (isThirdParty ? '3rd Party' : '1st Party') + ' - Bundled'
  }
  else {
    descriptionSuffix = typeDescription.split('-')[1];
  }
  return descriptionSuffix;
}
